/**
 * PmsAllocation — Allocation Engine V1 (visual).
 *
 * Reads the same reservation dataset that the calendar/dashboard use, so a
 * villa's revenue on the ranking always matches its bars on the calendar.
 *
 * Sections:
 *   1. Pool tabs (Vistas / Privè)
 *   2. Pool summary — accommodation revenue, average occupancy, next
 *      recommended villa
 *   3. Ranking table (revenue · occupancy · owner days · maint · fairness)
 *   4. Cómo se calcula
 *   5. Simulador
 *   6. Últimas decisiones + override rate
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Section } from "@/pages/Shell";
import PmsShell from "@/pages/admin/pms/PmsShell";
import {
  Scales, Info, Sparkle, ArrowRight, MagicWand, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { POOLS, DEMO_MONTH, DEMO_MONTH_LABEL, DEMO_TODAY_ISO } from "@/data/pmsDemoData";
import {
  allocationRanking, allocationHistory, simulateAllocation,
  getVilla, getPool, formatEUR, formatPct, addDays,
} from "@/lib/pmsSelectors";
import { usePmsBlocks, usePmsReservations } from "@/lib/pmsRuntimeStore";

export default function PmsAllocation() {
  const [poolId, setPoolId] = useState("vistas");
  // Suscripción al runtime store: nuevos blocks o reservas provocan que el
  // ranking, el simulador y la lista de "últimas decisiones" se recomputen.
  const blocks = usePmsBlocks();
  const reservations = usePmsReservations();

  const ranking = useMemo(
    () => allocationRanking(poolId, DEMO_MONTH),
    [poolId, blocks, reservations],
  );
  const history = useMemo(() => allocationHistory(DEMO_MONTH), [reservations]);

  const poolRevenue = ranking.reduce((acc, r) => acc + r.month.revenue_month, 0);
  const poolAvgOccupancy = ranking.length > 0
    ? ranking.reduce((acc, r) => acc + r.month.occupancy, 0) / ranking.length
    : 0;
  const nextRecommended = ranking[0]?.villa;

  return (
    <PmsShell>
      <Section
        title="Allocation Engine"
        subtitle={`Reparto justo de oportunidades dentro del mismo pool · ${DEMO_MONTH_LABEL}.`}
      >
        {/* Pool tabs */}
        <div className="mb-4 inline-flex bg-stone-100 dark:bg-stone-800 rounded-full p-0.5" data-testid="pms-allocation-pool-tabs">
          {POOLS.map((p) => {
            const active = poolId === p.pool_id;
            return (
              <button
                key={p.pool_id}
                onClick={() => setPoolId(p.pool_id)}
                data-testid={`pms-allocation-pool-${p.pool_id}`}
                className={`text-xs px-4 py-1.5 rounded-full ${
                  active
                    ? "bg-white dark:bg-stone-950 text-stone-900 dark:text-stone-100 shadow-sm"
                    : "text-stone-500 dark:text-stone-400"
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Pool summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="pms-allocation-pool-summary">
          <Kpi label="Ingresos pool"          value={formatEUR(poolRevenue)} sub={`${DEMO_MONTH_LABEL}`} />
          <Kpi label="Ocupación media"        value={formatPct(poolAvgOccupancy)} sub={`${ranking.length} villas`} />
          <Kpi label="Siguiente recomendada"  value={nextRecommended ? `${nextRecommended.villa_id}` : "—"} sub={nextRecommended?.name || ""} tone="terracotta" />
          <Kpi label="Override rate"          value={formatPct(history.override_rate, 0)} sub={`${history.overrides}/${history.total} decisiones`} />
        </div>

        {/* Ranking */}
        <SubHeading>Ranking del pool · orden por fairness (menor performance primero)</SubHeading>
        <div className="dv-card p-0 overflow-x-auto mb-6" data-testid="pms-allocation-ranking">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500 border-b border-stone-200 dark:border-stone-800">
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Villa</th>
                <th className="px-3 py-2 text-right">Revenue mes</th>
                <th className="px-3 py-2 text-right">Occupancy</th>
                <th className="px-3 py-2 text-right">Owner d.</th>
                <th className="px-3 py-2 text-right">Maint · comp.</th>
                <th className="px-3 py-2 text-right">Perf. score</th>
                <th className="px-3 py-2 text-right">Fairness</th>
              </tr>
            </thead>
            <tbody>
              {ranking.map((row, i) => (
                <tr
                  key={row.villa.villa_id}
                  className={`border-b border-stone-100 dark:border-stone-900 ${
                    i === 0 ? "bg-amber-50 dark:bg-amber-950/20" : ""
                  }`}
                  data-testid={`pms-alloc-row-${row.villa.villa_id}`}
                >
                  <td className="px-3 py-2 text-stone-500 dark:text-stone-400 tabular-nums">{i + 1}</td>
                  <td className="px-3 py-2">
                    <div className="text-sm text-stone-900 dark:text-stone-100">
                      <span className="text-[10px] text-stone-400 mr-1">{row.villa.villa_id}</span>
                      {row.villa.name}
                    </div>
                    {i === 0 && (
                      <div className="text-[10px] uppercase tracking-widest text-amber-800 dark:text-amber-200 mt-0.5 inline-flex items-center gap-1">
                        <Sparkle size={10} weight="fill" /> Siguiente recomendada
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-stone-800 dark:text-stone-200">{formatEUR(row.month.revenue_month)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-stone-800 dark:text-stone-200">{formatPct(row.month.occupancy)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-stone-500 dark:text-stone-400">{row.month.owner_days}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-stone-500 dark:text-stone-400">
                    {row.month.maintenance_days_compensable}
                    {row.month.maintenance_days_compensable > 0 && (
                      <span className="ml-1 text-[9px] uppercase tracking-widest text-emerald-700 dark:text-emerald-300">↺</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-stone-800 dark:text-stone-200">{(row.performance_score * 100).toFixed(1)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    <FairnessBar value={row.fairness_index} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cómo se calcula */}
        <div className="dv-card mb-6 border-l-4 border-l-amber-400" data-testid="pms-allocation-how">
          <div className="flex items-start gap-3">
            <Info size={16} className="text-amber-600 dark:text-amber-400 mt-0.5" weight="fill" />
            <div className="text-sm text-stone-700 dark:text-stone-300 space-y-2">
              <div className="font-semibold text-stone-900 dark:text-stone-100">Cómo se calcula</div>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Performance</strong>: 70 % revenue · 30 % ocupación.</li>
                <li><strong>Horizonte</strong>: 70 % mes actual · 30 % YTD.</li>
                <li><strong>Revenue</strong> usado = accommodation revenue bruto (antes de comisión OTA, direct fee, opex, management).</li>
                <li><strong>Canal</strong> no afecta el score — Booking, Airbnb y Direct compiten en igualdad.</li>
                <li><strong>Owner stays</strong>: reducen disponibilidad y <em>no</em> son compensables.</li>
                <li><strong>Maintenance</strong> no imputable al propietario: puede marcarse <code>fairness_compensable=true</code> y el motor ajusta la disponibilidad para no penalizar a esa villa.</li>
                <li><strong>Tie breakers</strong>: menos noches futuras reservadas · más tiempo desde la última asignación · villa_id determinista. Nunca aleatorio.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Simulador */}
        <SubHeading>Simular asignación</SubHeading>
        <AllocationSimulator poolId={poolId} />

        {/* History */}
        <SubHeading>Últimas decisiones</SubHeading>
        <div className="dv-card p-0" data-testid="pms-allocation-history">
          <ul className="divide-y divide-stone-200 dark:divide-stone-800">
            {history.rows.slice(0, 8).map((row) => (
              <HistoryRow key={row.reservation_id} row={row} />
            ))}
          </ul>
        </div>
      </Section>
    </PmsShell>
  );
}

function SubHeading({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500 mb-2 mt-1">
      {children}
    </div>
  );
}

function Kpi({ label, value, sub, tone = "stone" }) {
  const toneClass = tone === "terracotta"
    ? "text-[#9C5236] dark:text-amber-300"
    : "text-stone-900 dark:text-stone-100";
  return (
    <div className="dv-card">
      <div className="text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-stone-500 dark:text-stone-400">{sub}</div>}
    </div>
  );
}

function FairnessBar({ value }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div className="inline-flex items-center gap-2">
      <div className="w-16 h-1.5 rounded-full bg-stone-100 dark:bg-stone-800 overflow-hidden">
        <div
          className="h-full bg-stone-800 dark:bg-stone-200"
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      </div>
      <span className="text-xs text-stone-500 dark:text-stone-400">{pct.toFixed(0)}</span>
    </div>
  );
}

function HistoryRow({ row }) {
  const villaAssigned = getVilla(row.assigned_villa_id);
  const villaRecommended = getVilla(row.recommended_villa_id);
  const override = row.mode === "manual_override";
  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to={`/pms/reservations/${row.reservation_id}`}
          className="font-mono text-xs text-stone-900 dark:text-stone-100 hover:underline"
        >
          {row.reservation_id}
        </Link>
        <span className="text-xs text-stone-500 dark:text-stone-400 tabular-nums">
          {row.check_in} · {getPool(row.pool_id)?.label}
        </span>
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${
          override
            ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
            : "bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300"
        }`}>
          {override ? "Manual override" : "Automática"}
        </span>
        <span className="text-xs text-stone-700 dark:text-stone-300 ml-auto">
          <span className="text-stone-500">Rec: </span>
          {villaRecommended?.villa_id || "—"}
          <span className="mx-2 text-stone-400">→</span>
          <span className="text-stone-500">Asig: </span>
          <span className="font-medium">{villaAssigned?.villa_id}</span>
        </span>
      </div>
      {override && row.reason && (
        <div className="mt-1 text-[11px] text-stone-500 dark:text-stone-400 italic">
          “{row.reason}”
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Simulator
// ---------------------------------------------------------------------------

function AllocationSimulator({ poolId }) {
  // Suscripción: si aparece un maintenance block nuevo o se crea una reserva
  // manual mientras el simulador está expuesto, el resultado se recalcula.
  const blocks = usePmsBlocks();
  const reservations = usePmsReservations();

  const defaultIn = addDays(DEMO_TODAY_ISO, 5);
  const defaultOut = addDays(DEMO_TODAY_ISO, 10);
  const [checkIn, setCheckIn] = useState(defaultIn);
  const [checkOut, setCheckOut] = useState(defaultOut);
  const [revenue, setRevenue] = useState(1200);
  const [ran, setRan] = useState(false);

  const result = useMemo(() => {
    if (!ran) return null;
    return simulateAllocation({
      pool_id: poolId,
      check_in: checkIn,
      check_out: checkOut,
      accommodation_revenue_eur: Number(revenue) || 0,
    });
  }, [ran, poolId, checkIn, checkOut, revenue, blocks, reservations]);

  const nightsPreview = checkIn < checkOut
    ? Math.round((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 86400000)
    : 0;

  return (
    <div className="dv-card mb-6" data-testid="pms-allocation-simulator">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500 mb-3">
        <MagicWand size={12} weight="bold" /> Simulador · sin persistencia
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SimField label="Check-in" >
          <input
            type="date"
            value={checkIn}
            onChange={(e) => { setCheckIn(e.target.value); setRan(false); }}
            className="w-full text-sm px-2 py-1.5 rounded border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 text-stone-800 dark:text-stone-200"
          />
        </SimField>
        <SimField label="Check-out">
          <input
            type="date"
            value={checkOut}
            onChange={(e) => { setCheckOut(e.target.value); setRan(false); }}
            className="w-full text-sm px-2 py-1.5 rounded border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 text-stone-800 dark:text-stone-200"
          />
        </SimField>
        <SimField label={`Accommodation revenue (EUR) · ${nightsPreview} noches`}>
          <input
            type="number"
            min={0}
            value={revenue}
            onChange={(e) => { setRevenue(e.target.value); setRan(false); }}
            className="w-full text-sm px-2 py-1.5 rounded border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 text-stone-800 dark:text-stone-200 tabular-nums"
          />
        </SimField>
        <div className="flex items-end">
          <button
            type="button"
            onClick={() => setRan(true)}
            className="w-full text-sm px-3 py-2 rounded bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 hover:opacity-90 inline-flex items-center justify-center gap-1.5"
            data-testid="pms-allocation-simulate-btn"
          >
            <Scales size={14} /> Calcular
          </button>
        </div>
      </div>

      {result && (
        <div className="mt-4 border-t border-stone-200 dark:border-stone-800 pt-4">
          {result.eligible_count === 0 ? (
            <div className="text-sm text-stone-500 dark:text-stone-400">
              Ninguna villa del pool tiene disponibilidad para ese rango.
            </div>
          ) : (
            <>
              <div className="text-sm text-stone-700 dark:text-stone-300 mb-3">
                Villas elegibles: <span className="font-medium">{result.eligible_count}</span> ·
                {" "}Recomendada: <span className="font-medium text-[#9C5236] dark:text-amber-300">
                  {result.recommended?.villa.villa_id} {result.recommended?.villa.name}
                </span>
              </div>
              <ul className="divide-y divide-stone-100 dark:divide-stone-900">
                {result.previews.map((p, i) => (
                  <li key={p.villa.villa_id} className="py-2 flex items-center gap-3">
                    <span className="w-6 text-xs text-stone-400 tabular-nums">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-stone-900 dark:text-stone-100">
                        {p.villa.villa_id} · {p.villa.name}
                      </div>
                      <div className="text-[11px] text-stone-500 dark:text-stone-400">
                        Futuras: {p.future_booked_nights}n · última asignación: {p.last_check_in.slice(5)}
                      </div>
                    </div>
                    <div className="text-xs text-right tabular-nums">
                      <div className="text-stone-500">Antes {(p.current_score * 100).toFixed(1)}</div>
                      <div className="text-stone-900 dark:text-stone-100">
                        Después {(p.estimated_score * 100).toFixed(1)}
                        <span className={`ml-1 ${p.score_delta > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                          {p.score_delta >= 0 ? "▲" : "▼"}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          {result.excluded && result.excluded.length > 0 && (
            <div className="mt-4 pt-3 border-t border-stone-100 dark:border-stone-900" data-testid="pms-allocation-excluded">
              <div className="text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500 mb-1">
                No elegibles
              </div>
              <ul className="text-xs text-stone-600 dark:text-stone-400 space-y-1">
                {dedupeExcluded(result.excluded).map((e) => (
                  <li key={`${e.villa.villa_id}-${e.reason}`}>
                    <span className="text-stone-500">{e.villa.villa_id}</span>{" "}
                    <span className="text-stone-900 dark:text-stone-100">{e.villa.name}</span>{" "}
                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-950/30 dark:text-red-300 ml-1">
                      {e.reason}
                    </span>
                    {e.date && (
                      <span className="text-stone-400 ml-1">· conflicto {e.date}</span>
                    )}
                    {e.ref && (
                      <span className="text-stone-400 ml-1">· {e.ref}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {!result && (
        <p className="mt-3 text-[11px] text-stone-500 dark:text-stone-400 inline-flex items-center gap-1">
          <ArrowRight size={12} /> Ajusta parámetros y pulsa <em>Calcular</em>. El simulador no crea la reserva.
        </p>
      )}

      <div className="mt-3 text-[11px] text-stone-400 dark:text-stone-500 inline-flex items-center gap-1">
        <ClockCounterClockwise size={12} /> Sin persistencia · los datos vuelven al recargar.
      </div>
    </div>
  );
}

function SimField({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

// Collapse repeated (villa, reason) pairs — el simulador puede empujar
// varios conflictos de la misma villa (una fecha por cada noche).
function dedupeExcluded(list) {
  const seen = new Set();
  const out = [];
  for (const e of list) {
    const key = `${e.villa.villa_id}-${e.reason}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}
